import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { GmailMailbox } from "../types/gmail.types";

/**
 * Mirrors the WhatsApp slice idea: persist active tab + light UI flags here
 * and let React Query own server data. Inbox messages are Gmail's string ids,
 * so they get their own selection next to the numeric Sent one.
 */

export interface ComposeState {
  open: boolean;
  minimized: boolean;
  fullscreen: boolean;
  templateId?: number;
  leadId?: number;
  dealId?: number;
  prefill?: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    htmlBody?: string;
  };
}

export interface GmailState {
  activeMailbox: GmailMailbox;
  selectedMessageId: number | null;
  selectedInboxId: string | null;
  searchQuery: string;
  compose: ComposeState;
}

const initialCompose: ComposeState = {
  open: false,
  minimized: false,
  fullscreen: false,
};

const initialState: GmailState = {
  activeMailbox: "inbox",
  selectedMessageId: null,
  selectedInboxId: null,
  searchQuery: "",
  compose: initialCompose,
};

const gmailSlice = createSlice({
  name: "gmail",
  initialState,
  reducers: {
    setActiveMailbox(state, action: PayloadAction<GmailMailbox>) {
      state.activeMailbox = action.payload;
      state.selectedMessageId = null;
      state.selectedInboxId = null;
    },
    setSelectedMessage(state, action: PayloadAction<number | null>) {
      state.selectedMessageId = action.payload;
    },
    setSelectedInboxMessage(state, action: PayloadAction<string | null>) {
      state.selectedInboxId = action.payload;
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },
    openCompose(state, action: PayloadAction<Partial<ComposeState> | undefined>) {
      state.compose = {
        ...initialCompose,
        ...(action.payload ?? {}),
        open: true,
        minimized: false,
      };
    },
    minimizeCompose(state, action: PayloadAction<boolean>) {
      state.compose.minimized = action.payload;
    },
    toggleComposeFullscreen(state) {
      state.compose.fullscreen = !state.compose.fullscreen;
      if (state.compose.fullscreen) state.compose.minimized = false;
    },
    closeCompose(state) {
      state.compose = initialCompose;
    },
  },
});

export const {
  setActiveMailbox,
  setSelectedMessage,
  setSelectedInboxMessage,
  setSearchQuery,
  openCompose,
  minimizeCompose,
  toggleComposeFullscreen,
  closeCompose,
} = gmailSlice.actions;

export default gmailSlice.reducer;
